/**
 * Presentational Share-watchlist modal (desktop, PRD-6.1 / SE-8313). Pure JSX —
 * all Apollo and grant orchestration live in the shared useShareWatchlist hook
 * consumed by the Container.
 *
 * Layout:
 * - Heading "Share <List>" with a close action.
 * - Invite row: people search, `Can edit` / `Can view`, Invite.
 * - WHO HAS ACCESS: owner first, then each sharee with an in-place role Select
 *   and a revoke action; "Private until you invite someone." while only the
 *   owner has access.
 * - Footer: Done. There is no Financials control on a list (PRD-6.3).
 * @module features/scouting/components/ShareWatchlistModal/ShareWatchlistModalView
 */

import { XIcon } from "lucide-react-native";
import { memo, type ReactNode } from "react";

import ShareAccessRow from "@/features/scouting/components/molecules/ShareAccessRow";
import ShareGroupAccessRow from "@/features/scouting/components/molecules/ShareGroupAccessRow";
import ShareInviteRow from "@/features/scouting/components/molecules/ShareInviteRow";

import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
} from "@/components/atoms/AlertDialog";
import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Heading } from "@/components/atoms/Heading";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import { SHARE_ACCESS_STATE_COPY } from "@/features/scouting/utils/shareWatchlistGrants";
import type {
  ShareGroupAccessRowItem,
  ShareGroupCandidate,
} from "@/features/scouting/utils/shareGroupGrants";
import type {
  ShareAccessRowItem,
  ShareAccessState,
  ShareCandidateItem,
  ShareGrantRole,
} from "@/features/scouting/utils/shareWatchlistGrants";

import {
  TEST_ID_SCENARIO_SHARE,
  TEST_ID_WATCHLIST_SHARE,
  TEST_ID_WATCHLIST_SHARE_MODAL,
} from "e2e/testId";

/** No group line: lists that predate SE-8359 or a closed sheet. */
const NO_GROUP_ROWS: readonly ShareGroupAccessRowItem[] = [];
/**
 * Inert group handlers for a caller that wires no group arm.
 * @returns Nothing.
 */
function NOOP_GROUP_ROLE(): undefined {
  return undefined;
}
/**
 * Inert group revoke for a caller that wires no group arm.
 * @returns Nothing.
 */
function NOOP_GROUP_REVOKE(): undefined {
  return undefined;
}

/** Props for {@link ShareWatchlistModalView}. */
export interface ShareWatchlistModalViewProps {
  /** Whether the modal is open. */
  readonly isOpen: boolean;
  /** List name for the heading. */
  readonly listName: string;
  /** WHO HAS ACCESS rows, owner first. */
  readonly accessRows: readonly ShareAccessRowItem[];
  /** Whether the rows are known yet; the empty state renders only when "ready". */
  readonly accessState?: ShareAccessState;
  /** Whether anyone besides the owner has access. */
  readonly hasSharees: boolean;
  /** Current invite search text. */
  readonly searchQuery: string;
  /** Suggestions for the current search. */
  readonly candidates: readonly ShareCandidateItem[];
  /** The picked suggestion, if any. */
  readonly selectedCandidate: ShareCandidateItem | null;
  /** Role the next invite grants. */
  readonly inviteRole: ShareGrantRole;
  /** Inline invite error, or null. */
  readonly inviteError: string | null;
  /** Whether an invite is in flight. */
  readonly isInviting: boolean;
  /** Search change handler. */
  readonly onSearchChange: (next: string) => void;
  /** Suggestion pick handler. */
  readonly onSelectCandidate: (userId: string) => void;
  /** Clears the picked suggestion. */
  readonly onClearCandidate: () => void;
  /** Invite role change handler. */
  readonly onInviteRoleChange: (value: string) => void;
  /** Invite press handler. */
  readonly onInvite: () => void;
  /** Role change handler keyed by grant id. */
  readonly onChangeRole: (userWatchlistId: string, value: string) => void;
  /** Revoke handler keyed by grant id. */
  readonly onRevoke: (userWatchlistId: string) => void;
  /** "Everyone at {organization}" while the owner may still invite it (SE-8359). */
  readonly groupCandidate?: ShareGroupCandidate | null;
  /** The picked group, if any. */
  readonly selectedGroup?: ShareGroupCandidate | null;
  /** Picks the group candidate. */
  readonly onSelectGroup?: () => void;
  /** WHO HAS ACCESS group lines, rendered above the people (SE-8359). */
  readonly groupRows?: readonly ShareGroupAccessRowItem[];
  /** Group role change handler keyed by group grant id. */
  readonly onChangeGroupRole?: (groupGrantId: string, value: string) => void;
  /** Group revoke handler keyed by group grant id. */
  readonly onRevokeGroup?: (groupGrantId: string) => void;
  /** Caption under the invite row (e.g. only Tier 3 matched), or null. */
  readonly inviteCaption?: string | null;
  /**
   * Entity-specific block rendered under WHO HAS ACCESS — the scenario
   * dialog's WHAT THEY CAN SEE section (SE-8356). Lists pass nothing.
   */
  readonly accessSection?: ReactNode;
  /** Done / dismiss. */
  readonly onClose: () => void;
}

/**
 * Renders the Share-watchlist AlertDialog.
 * @param root0 - View props
 * @param root0.isOpen - Whether the modal is open
 * @param root0.listName - List name for the heading
 * @param root0.accessRows - WHO HAS ACCESS rows
 * @param root0.accessState - Whether the rows are known yet
 * @param root0.hasSharees - Whether anyone besides the owner has access
 * @param root0.searchQuery - Current invite search text
 * @param root0.candidates - Suggestions for the current search
 * @param root0.selectedCandidate - The picked suggestion
 * @param root0.inviteRole - Role the next invite grants
 * @param root0.inviteError - Inline invite error
 * @param root0.isInviting - Whether an invite is in flight
 * @param root0.onSearchChange - Search change handler
 * @param root0.onSelectCandidate - Suggestion pick handler
 * @param root0.onClearCandidate - Clears the picked suggestion
 * @param root0.onInviteRoleChange - Invite role change handler
 * @param root0.onInvite - Invite press handler
 * @param root0.onChangeRole - Role change handler keyed by grant id
 * @param root0.onRevoke - Revoke handler keyed by grant id
 * @param root0.groupCandidate - The group candidate, or null
 * @param root0.selectedGroup - The picked group, or null
 * @param root0.onSelectGroup - Picks the group candidate
 * @param root0.groupRows - WHO HAS ACCESS group lines
 * @param root0.onChangeGroupRole - Group role change handler
 * @param root0.onRevokeGroup - Group revoke handler
 * @param root0.inviteCaption - Caption under the invite row
 * @param root0.accessSection - Entity-specific block under WHO HAS ACCESS
 * @param root0.onClose - Done / dismiss
 * @returns The rendered modal.
 */
const ShareWatchlistModalView = ({
  isOpen,
  listName,
  accessRows,
  accessState = "ready",
  hasSharees,
  searchQuery,
  candidates,
  selectedCandidate,
  inviteRole,
  inviteError,
  isInviting,
  onSearchChange,
  onSelectCandidate,
  onClearCandidate,
  onInviteRoleChange,
  onInvite,
  onChangeRole,
  onRevoke,
  groupCandidate = null,
  selectedGroup = null,
  onSelectGroup,
  groupRows = NO_GROUP_ROWS,
  onChangeGroupRole = NOOP_GROUP_ROLE,
  onRevokeGroup = NOOP_GROUP_REVOKE,
  inviteCaption = null,
  accessSection = null,
  onClose,
}: ShareWatchlistModalViewProps) => (
  <AlertDialog isOpen={isOpen} onClose={onClose} size="md">
    <AlertDialogBackdrop />
    <AlertDialogContent
      testID={TEST_ID_WATCHLIST_SHARE_MODAL.ROOT}
      className="w-full max-w-lg p-0"
    >
      {/* SE-8526 — organisms/ShareDialog (Figma 970:19119): p 24, gap 20,
          title-sm heading with the name in quotes, a bare muted ✕. */}
      <AlertDialogBody className="p-6">
        <Stack className="gap-5">
          <Stack direction="horizontal" className="items-center gap-2">
            <Heading
              variant="title-sm"
              className="min-w-0 flex-1 text-content-primary"
            >
              {`Share “${listName}”`}
            </Heading>
            <Pressable
              testID={TEST_ID_WATCHLIST_SHARE_MODAL.CLOSE_BUTTON}
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel="Close"
              className="web:hover:bg-surface-raised web:transition-colors size-8 shrink-0 items-center justify-center rounded-sm"
            >
              <Icon as={XIcon} size="sm" className="text-content-muted" />
            </Pressable>
          </Stack>

          <ShareInviteRow
            searchQuery={searchQuery}
            candidates={candidates}
            selectedCandidate={selectedCandidate}
            inviteRole={inviteRole}
            inviteError={inviteError}
            isInviting={isInviting}
            onSearchChange={onSearchChange}
            onSelectCandidate={onSelectCandidate}
            onClearCandidate={onClearCandidate}
            onInviteRoleChange={onInviteRoleChange}
            onInvite={onInvite}
            groupCandidate={groupCandidate}
            selectedGroup={selectedGroup}
            onSelectGroup={onSelectGroup}
          />
          {inviteCaption == null ? null : (
            <Box testID={TEST_ID_SCENARIO_SHARE.INVITE_CAPTION}>
              <Text variant="caption" className="text-content-muted">
                {inviteCaption}
              </Text>
            </Box>
          )}

          <Stack className="gap-2">
            <Text
              variant="micro-strong"
              className="uppercase text-content-muted"
            >
              Who has access
            </Text>
            {/* Figma 970:19119 sets the privacy line right under the heading.
                It still shows only while nobody else has access — with a
                sharee it would no longer be true. */}
            {accessState === "ready" && !hasSharees ? (
              <Box testID={TEST_ID_WATCHLIST_SHARE.ACCESS_EMPTY}>
                <Text variant="caption" className="text-content-muted">
                  Private until you invite someone.
                </Text>
              </Box>
            ) : null}
            <Box
              testID={TEST_ID_WATCHLIST_SHARE.ACCESS_LIST}
              className="max-h-80"
            >
              <ScrollView keyboardShouldPersistTaps="handled">
                <Stack className="gap-2 pr-2">
                  {groupRows.map(row => (
                    <ShareGroupAccessRow
                      key={row.groupGrantId}
                      row={row}
                      onChangeRole={onChangeGroupRole}
                      onRevoke={onRevokeGroup}
                    />
                  ))}
                  {accessRows.map(row => (
                    <ShareAccessRow
                      key={row.userWatchlistId}
                      row={row}
                      onChangeRole={onChangeRole}
                      onRevoke={onRevoke}
                    />
                  ))}
                </Stack>
              </ScrollView>
            </Box>
            {accessState === "ready" ? null : (
              <Box testID={TEST_ID_WATCHLIST_SHARE.ACCESS_STATE}>
                <Text variant="caption" className="text-content-muted">
                  {SHARE_ACCESS_STATE_COPY[accessState]}
                </Text>
              </Box>
            )}
          </Stack>
          {accessSection}
        </Stack>
      </AlertDialogBody>

      <AlertDialogFooter className="px-6 pb-6 pt-0">
        <Stack direction="horizontal" className="w-full justify-end">
          <Button
            testID={TEST_ID_WATCHLIST_SHARE.DONE_BUTTON}
            label="Done"
            tone="primary"
            onPress={onClose}
            className="web:transition-colors rounded-sm bg-accent-primary data-[hover=true]:bg-accent-hover"
          />
        </Stack>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

ShareWatchlistModalView.displayName = "ShareWatchlistModalView";

export default memo(ShareWatchlistModalView);
